import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import "./Login.css";
import Swal from 'sweetalert2';
import { debounce } from 'lodash';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ResetPassword() {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [token, setToken] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [message, setMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState("");

    const navigate = useNavigate();
    const location = useLocation();
    const { authFetch, refreshCsrfToken, csrfToken } = useContext(AuthContext);

    useEffect(() => {
        if (!csrfToken) {
            refreshCsrfToken();
        }
    }, [csrfToken, refreshCsrfToken]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get("token");
        const emailParam = params.get("email");

        if (!tokenParam || !emailParam) {
            setValidatingToken(false);
            setTokenValid(false);
            setMessage("Invalid reset link. Please request a new password reset.");
            setIsError(true);
            return;
        }

        setToken(tokenParam);
        setEmail(emailParam);

        const verifyToken = async () => {
            try {
                const response = await authFetch(`${API_BASE_URL}/Users/password/verify-token`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token: tokenParam, email: emailParam }),
                });

                const data = await response.json();

                if (response.ok && data.valid) {
                    setTokenValid(true);
                } else {
                    setTokenValid(false);
                    setMessage(data.message || "Invalid or expired reset link. Please request a new password reset.");
                    setIsError(true);
                }
            } catch (error) {
                setTokenValid(false);
                setMessage("Error verifying reset token. Please try again later.");
                setIsError(true);
            } finally {
                setValidatingToken(false);
            }
        };

        verifyToken();
    }, [location, authFetch]);

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);

        if (!passwordVisible) {
            setTimeout(() => {
                setPasswordVisible(false);
            }, 2000);
        }
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);

        if (!confirmPasswordVisible) {
            setTimeout(() => {
                setConfirmPasswordVisible(false);
            }, 2000);
        }
    };

    const checkPasswordStrength = debounce((password) => {
        if (!password) {
            setPasswordStrength(0);
            setPasswordFeedback("");
            return;
        }

        let score = 0;

        if (password.length >= 6) score += 1;
        if (password.length >= 10) score += 1;

        if (/[0-9]/.test(password)) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[^A-Za-z0-9]/.test(password)) score += 1;

        score = Math.min(score, 4);

        setPasswordStrength(score);

        if (score <= 1) {
            setPasswordFeedback("Consider adding numbers or making it longer.");
        } else if (score === 2) {
            setPasswordFeedback("Password could be stronger");
        } else {
            setPasswordFeedback("Good password strength!");
        }
    }, 300);

    useEffect(() => {
        checkPasswordStrength(password);

        return () => {
            checkPasswordStrength.cancel();
        };
    }, [password, checkPasswordStrength]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setIsError(true);
            setMessage("Passwords do not match.");
            return;
        }

        setLoading(true);
        setMessage("");
        setIsError(false);

        try {
            const response = await authFetch(`${API_BASE_URL}/Users/password/reset`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token, email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.message || "Password reset successful.");

                Swal.fire({
                    title: 'Success!',
                    text: 'Password reset successfully. You can now log in with your new password.',
                    icon: 'success',
                    confirmButtonColor: '#2486d2',
                    confirmButtonText: 'Go to Login'
                }).then(() => {
                    navigate('/login');
                });
            } else {
                if (response.status === 429) {
                    setIsError(true);
                    setMessage("Too many attempts. Please try again later.");
                } else {
                    setIsError(true);
                    setMessage(data.message || "Password reset failed. Please try again.");
                }
            }
        } catch (error) {
            setIsError(true);
            setMessage("Connection error. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const renderPasswordStrengthBar = () => {
        if (!password || typeof passwordStrength !== 'number') {
            return null;
        }

        const containerStyle = {
            width: '100%',
            marginTop: '10px',
            marginBottom: '10px'
        };

        const barStyle = {
            display: 'flex',
            height: '8px',
            borderRadius: '4px',
            overflow: 'hidden',
            backgroundColor: '#e0e0e0',
            marginBottom: '5px'
        };

        const segmentStyle = {
            flex: 1,
            margin: '0 2px'
        };

        const strengthColors = [
            '#ff4d4d', // very weak - red
            '#ffaa00', // weak - orange
            '#ffff00', // fair - yellow
            '#00cc00', // good - green
            '#00aaff'  // strong - blue
        ];

        const feedbackStyle = {
            fontSize: '12px',
            color: '#666',
            marginTop: '5px'
        };

        return (
            <div style={containerStyle}>
                <div style={barStyle}>
                    {[...Array(5)].map((_, index) => (
                        <div
                            key={index}
                            style={{
                                ...segmentStyle,
                                backgroundColor: index < passwordStrength + 1 ? strengthColors[index] : 'transparent',
                            }}
                        />
                    ))}
                </div>
                {passwordFeedback && (
                    <div style={feedbackStyle}>
                        {passwordFeedback}
                    </div>
                )}
            </div>
        );
    };

    if (loading || validatingToken) {
        return (
            <div className="user-login-container">
                <div className="user-login-loading">
                    <div className="user-login-spinner"></div>
                    <p>{validatingToken ? "Validating your reset link..." : "Processing your request..."}</p>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="user-login-container">
                <div className="user-login-wrapper">
                    <div className="user-login-mobile-form-container">
                        <div className="user-login-form-box">
                            <div className="user-login-form">
                                <h1>Invalid Reset Link</h1>
                                <div className="user-login-error">
                                    {message || "Invalid or expired reset link."}
                                </div>
                                <p>Please request a new password reset.</p>
                                <div className="user-login-mobile-switch">
                                    <Link to="/login" className="user-login-text-button">
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="user-login-container">
            <div className="user-login-wrapper">
                <div className="user-login-mobile-form-container">
                    <div className="user-login-form-box">
                        <form onSubmit={handleSubmit} className="user-login-form">
                            <h1>Set New Password</h1>
                            <span>Create a new password for your account</span>

                            {message && (
                                <div className={isError ? "user-login-error" : "user-login-success"}>
                                    {message}
                                </div>
                            )}

                            <div className="user-login-input-group">
                                <div className="user-login-password-field">
                                    <input
                                        type={passwordVisible ? "text" : "password"}
                                        className="user-login-input"
                                        placeholder="New Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="user-login-password-toggle"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {passwordVisible ? "Hide" : "Show"}
                                    </button>
                                </div>

                                {password && renderPasswordStrengthBar()}

                                <div className="user-login-password-field">
                                    <input
                                        type={confirmPasswordVisible ? "text" : "password"}
                                        className="user-login-input"
                                        placeholder="Confirm New Password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="user-login-password-toggle"
                                        onClick={toggleConfirmPasswordVisibility}
                                    >
                                        {confirmPasswordVisible ? "Hide" : "Show"}
                                    </button>
                                </div>
                            </div>

                            <button type="submit" className="user-login-button">
                                Reset Password
                            </button>

                            <div className="user-login-mobile-switch">
                                <Link to="/login" className="user-login-text-button">
                                    Back to Login
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;